/**
 * Shared Managed Dropdown Component
 *
 * Provides consistent "Manage" dropdown across sections with:
 * - Freeze/Activate actions
 * - Delete action
 * - Optional status change actions (Seeds)
 *
 * Sections control business logic through handler props.
 * Component handles UI presentation and consistency.
 *
 * @example
 * ```tsx
 * // Assets usage
 * <ManagedDropdown
 *   onFreezeClick={handlers.handleFreeze}
 *   onActivateClick={handlers.handleActivate}
 *   onDeleteClick={handlers.handleDelete}
 *   shortcuts={{ freeze: '⌥ F', activate: '⌥ U', delete: '⌥ D' }}
 * />
 *
 * // Seeds usage with status options
 * <ManagedDropdown
 *   onFreezeClick={handlers.handleFreeze}
 *   onActivateClick={handlers.handleActivate}
 *   onDeleteClick={handlers.handleDelete}
 *   onStatusChangeClick={(status) => handlers.handleStatusChange(status)}
 *   statusOptions={[
 *     { label: 'Approve', value: 'accept', icon: <CheckCircle />, shortcut: '⌥ A' },
 *     { label: 'Reject', value: 'reject', icon: <XCircle />, shortcut: '⌥ R' }
 *   ]}
 *   shortcuts={{ freeze: '⌥ F', delete: '⌥ D' }}
 * />
 * ```
 *
 * @module components/actions
 */

import { EllipsisHorizontalCircleIcon } from '@heroicons/react/24/solid';
import { useMemo } from 'react';

import { ACTION_ICONS } from '@/components/actions/constants/actionIcons';
import type { ManagedDropdownProps } from '@/components/actions/types';
import { Dropdown } from '@/components/Dropdown';
import type { DropdownOption } from '@/types/dropdown.types';
import { cn } from '@/utils/classname';

/**
 * Default labels for managed actions
 */
const DEFAULT_LABELS = {
  freeze: 'Freeze',
  activate: 'Activate',
  delete: 'Delete',
} as const;

/**
 * Managed Dropdown Component
 *
 * Renders a dropdown with Freeze, Activate, Delete, and optionally Status Change actions.
 * Handlers must be provided by parent component (section-level).
 *
 * @param props - Component props
 * @returns Dropdown component
 */
export function ManagedDropdown({
  onFreezeClick,
  onActivateClick,
  onDeleteClick,
  onStatusChangeClick,
  statusOptions,
  labels,
  shortcuts,
  hideActions,
  label = 'Manage',
  disabled = false,
  className,
  'data-testid': testId = 'managed-dropdown',
}: ManagedDropdownProps) {
  /**
   * Build dropdown options based on configuration
   *
   * Memoized to prevent unnecessary re-renders when handlers change.
   * Only rebuilds when labels, shortcuts, or hideActions change.
   */
  const options = useMemo<DropdownOption[]>(() => {
    const items: DropdownOption[] = [];

    // Status change actions (Seeds only)
    if (!hideActions?.statusChange && statusOptions && onStatusChangeClick) {
      statusOptions.forEach(statusOption => {
        items.push({
          label: statusOption.label,
          icon: statusOption.icon,
          onClick: () => onStatusChangeClick(statusOption.value),
          disabled: disabled,
          count: statusOption.shortcut,
        });
      });

      // Divider after status options
      if (statusOptions.length > 0) {
        items.push({
          label: '',
          onClick: () => {},
          isDivider: true,
        });
      }
    }

    // Freeze action
    if (!hideActions?.freeze) {
      items.push({
        label: labels?.freeze ?? DEFAULT_LABELS.freeze,
        icon: <ACTION_ICONS.FREEZE className="size-4" />,
        onClick: onFreezeClick,
        disabled: disabled,
        count: shortcuts?.freeze,
      });
    }

    // Activate action
    if (!hideActions?.activate) {
      items.push({
        label: labels?.activate ?? DEFAULT_LABELS.activate,
        icon: <ACTION_ICONS.ACTIVATE className="size-4" />,
        onClick: onActivateClick,
        disabled: disabled,
        count: shortcuts?.activate,
      });
    }

    // Divider before delete (if we have actions above)
    if (items.length > 0 && !hideActions?.delete) {
      items.push({
        label: '',
        onClick: () => {},
        isDivider: true,
      });
    }

    // Delete action
    if (!hideActions?.delete) {
      items.push({
        label: labels?.delete ?? DEFAULT_LABELS.delete,
        icon: <ACTION_ICONS.DELETE className="size-4" />,
        onClick: onDeleteClick,
        disabled: disabled,
        count: shortcuts?.delete,
      });
    }

    return items;
  }, [
    labels,
    shortcuts,
    hideActions,
    disabled,
    statusOptions,
    onFreezeClick,
    onActivateClick,
    onDeleteClick,
    onStatusChangeClick,
  ]);

  return (
    <div data-testid={testId}>
      <Dropdown
        title="Manage Options"
        size="xxs"
        options={options}
        placeholder={label}
        styleType="rounded"
        border={false}
        disabled={disabled}
        className={cn('[&_span:nth-child(3)]:hidden', className)}
        listClassName="!z-[150]"
        icon={<EllipsisHorizontalCircleIcon className="size-4" />}
      />
    </div>
  );
}
